'use client'

import { setCookie, useGetCookie } from 'cookies-next'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { resetAppState } from '../_store'
import { sessionActions } from '../_store/session/session.slice'
import {
  saveFCM,
  setActiveGeometries,
  setCountry,
  setWebSettings,
} from '../_store/settings/settings.slice'

import getSiteSettings, {
  getSiteSettingsWithDomain,
  getSourceSettings,
  getTgBotSettings,
} from '../_service/settings.service'

import { sources } from '@/_utils/platform'
import { useRouter } from '@/i18n/navigation'
import { countryGeometries } from '../_constants'
import referalService from '../_service/referal.service'
import { AppDispatch } from '../_store/index'
import { IRedux } from '../_store/types'
import { utmActions } from '../_store/utm/utm.slice'
import { ISourceSettings, IWebSettings } from '../_types'
import { getPlatform } from '../_utils/platform'
import { track } from '../_utils/utm'
import { useTelegram } from './telegram'

const SESSION_COOKIE_OPTIONS = {
  maxAge: 30 * 24 * 60 * 60,
  path: '/',
} as const

const trackEnterance = async (
  {
    fcm_token,
    tg_chat_id,
    device_id,
    source_id,
    platform_id,
  }: Record<string, string>,
  referal: string | null | undefined,
  dispatch: AppDispatch,
) => {
  try {
    let referal_url_id: string = ''

    if (referal) {
      const response = await referalService
        .getUnique(referal)
        .then((res) => res.data)
        .catch((res) => res.data)
      referal_url_id = response?.id
    }
    await track({
      action_type: 'enter',
      referal_url_id,
      order_id: '',
      platform_id,
      device_id,
      fcm_token,
      source_id,
      tg_chat_id,
    })

    if (referal_url_id) {
      dispatch(utmActions.setReferralID(referal_url_id))
    }
  } catch (error) {
    console.error(error)
  }
}

const updateSessionFromParams = (
  dispatch: AppDispatch,
  params: URLSearchParams,
) => {
  const keys = [
    'crm_table_number',
    'crm_table_id',
    'menu_id',
    'branch_id',
    'shipper_id',
    'chat_id',
    'is_hall',
    'source',
  ]

  const sessionUpdate: Record<string, string> = {}

  keys.forEach((key) => {
    const value = params.get(key)
    if (value) {
      setCookie(key, value, SESSION_COOKIE_OPTIONS)
      sessionUpdate[key] = value
    }
  })

  if (Object.keys(sessionUpdate).length > 0) {
    dispatch(sessionActions.save(sessionUpdate))
  }
}

const getDeviceId = async () => {
  // Wait for idle time
  await new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(resolve)
    } else {
      setTimeout(resolve, 100)
    }
  })
  const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default
  const fp = await FingerprintJS.load()
  return (await fp.get()).visitorId
}

function useInitialSetup() {
  const searchParams = useSearchParams()
  const { tg_chat_id, webApp } = useTelegram()
  const sessionData = useSelector((state: IRedux) => state.session)
  const utm = useSelector((state: IRedux) => state.utm)

  const finalTgChatId = useMemo(() => {
    return tg_chat_id || searchParams.get('tg_chat_id') || ''
  }, [tg_chat_id, searchParams])

  const isBot = useMemo(() => Boolean(finalTgChatId), [finalTgChatId])

  return useMemo(() => {
    const source = isBot
      ? 'bot'
      : searchParams.get('platform_id') ||
        searchParams.get('source') ||
        sessionData.source
    const ref =
      (isBot
        ? searchParams.get('referal_url_id') ||
          webApp?.initDataUnsafe?.start_param
        : searchParams.get('ref')) || utm?.referal_url_id

    const shipperId = searchParams.get('shipper_id') || sessionData.shipper_id
    const isHall = searchParams.get('is_hall') || sessionData.is_hall
    // Persist across pages: prefer URL, then saved session (set on first load)
    const fcmToken = searchParams.get('fcm_token') || undefined

    return {
      source,
      ref,
      shipperId,
      isHall,
      fcmToken,
      finalTgChatId,
      isBot,
      searchParams,
      webApp,
    }
  }, [isBot, searchParams, sessionData, utm, webApp, finalTgChatId])
}

async function fetchAndSetSettings(
  dispatch: AppDispatch,
  router: ReturnType<typeof useRouter>,
  isWebapp: boolean,
  shipperId: string | null,
  isBot: boolean,
  platformId: string,
  isHall: string,
  currentOrderType: IRedux['session']['order_type'],
) {
  try {
    // 1. Get site settings (by shipperId if webapp, else by domain)
    let siteSettings: IWebSettings | null = null
    let isChooseDeliveryBranch = false

    if (shipperId) {
      siteSettings = await getSiteSettings(shipperId)
    } else {
      siteSettings =
        (
          await getSiteSettingsWithDomain(
            process.env.NEXT_PUBLIC_DELEVER_DOMAIN!,
          )
        )?.data || null
    }

    // 2. Determine final shipperId
    const currentShipperId = shipperId || siteSettings?.shipper_id
    if (!currentShipperId) {
      console.warn('⚠️ No currentShipperId available, skipping initialization')
      return
    }

    // 3. Set shipper_id cookie
    setCookie('shipper_id', currentShipperId, SESSION_COOKIE_OPTIONS)

    // 5. Set country if available
    const country = siteSettings?.data?.country
    if (country) {
      const { iso_code, currency } = country
      dispatch(setCountry({ iso_code, currency }))
    }

    // 6. Set web settings (prefer shipperSettings.data, else siteSettings.data)
    let webSettings: IWebSettings['data'] | undefined = isWebapp
      ? {
          logo: siteSettings?.data?.logo,
          shipper_name: siteSettings?.data?.shipper_name,
          is_active: true,
        }
      : siteSettings?.data

    const source = sources(platformId, isHall === 'true')
    if (source !== 'website') {
      // 7. Get source settings
      let sourceSettings: ISourceSettings | null = null
      try {
        if (currentShipperId) {
          sourceSettings = await getSourceSettings(currentShipperId).then(
            (res) => res.data,
          )
        }
      } catch (error) {
        console.error('❌ getSourceSettings ERROR:', error)
      }

      const sourceData = sourceSettings?.sources?.find(
        (item) => item?.source === source,
      )
      // set is_promocode from source-settigns if it is not website
      if (sourceData?.source) {
        webSettings = {
          ...webSettings,
          payment_types: sourceData?.payment_types_obj,
          order_types: sourceData?.order_types_obj,
          is_promocode: !!sourceData?.is_promocode,
        }
      }
    }

    if (isBot && currentShipperId) {
      try {
        const botSettings = await getTgBotSettings(currentShipperId)
        webSettings = {
          ...webSettings,
          payment_types: botSettings?.data?.payment_types,
          order_types: botSettings?.data?.order_types,
          is_promocode: botSettings?.data?.scenes?.includes('promo-code'),
        }
        isChooseDeliveryBranch = botSettings?.data?.scenes?.includes(
          'choose-delivery-branch',
        )
      } catch (error) {
        console.error('❌ getTgBotSettings ERROR:', error)
      }
    }

    if (webSettings) {
      if (webSettings?.is_active === false) {
        return router.push('/temporary')
      }
      // settings color
      webSettings = {
        ...webSettings,
        color: siteSettings?.data?.color || '#000',
        cashback_display_places: siteSettings?.data?.cashback_display_places,
      }

      dispatch(
        setWebSettings({
          shipper_id: currentShipperId!,
          data: webSettings,
          choose_delivery_branch: isChooseDeliveryBranch,
        }),
      )
      if (webSettings?.default_menu?.value) {
        setCookie(
          'default_menu_id',
          webSettings?.default_menu?.value,
          SESSION_COOKIE_OPTIONS,
        )
      }
    }

    const activeLocation = countryGeometries['UZ']

    if (activeLocation) {
      dispatch(setActiveGeometries(activeLocation))
    }

    // Default to delivery order-type object when session has none yet.
    if (webSettings?.order_types?.length && !currentOrderType) {
      const delivery = webSettings.order_types.find(
        (item) => item.type === 'delivery',
      )
      dispatch(
        sessionActions.saveOrderType(delivery || webSettings.order_types[0]),
      )
    }
  } catch (error) {
    console.error('💥 Failed to fetch settings:', error)
    if ((error as any)?.status === 404) {
      // Hard navigation so the document response is a real HTTP 404.
      const match = window.location.pathname.match(/^\/(en|uz)(?=\/|$)/)
      const prefix = match?.[0] ?? ''
      window.location.assign(`${prefix}/not-found`)
    }
  }
}

export default function InitializeSettings() {
  const router = useRouter()
  const getCookie = useGetCookie()

  const hash = useRef('')
  const initialized = useRef(false)
  const dispatch = useDispatch()

  const {
    searchParams,
    source,
    ref,
    shipperId,
    isHall,
    fcmToken,
    finalTgChatId,
    isBot,
    webApp,
  } = useInitialSetup()

  const settings = useSelector((state: IRedux) => state.settings)
  const common = useSelector((state: IRedux) => state.common)
  const sessionData = useSelector((state: IRedux) => state.session)

  const fetchData = useCallback(async () => {
    const currentHash = String(ref + source + finalTgChatId)
    if (initialized.current && hash.current === currentHash) return
    initialized.current = true
    hash.current = currentHash

    updateSessionFromParams(dispatch, searchParams)

    const platformId = getPlatform({
      is_hall: String(isHall),
      platform_id: source,
    })

    if (fcmToken) {
      dispatch(saveFCM(fcmToken))
      dispatch(sessionActions.save({ fcm_token: fcmToken }))
    }

    // Fetch settings on client-side
    await fetchAndSetSettings(
      dispatch,
      router,
      false,
      shipperId,
      isBot,
      platformId,
      isHall,
      sessionData.order_type,
    )
  }, [
    dispatch,
    searchParams,
    source,
    ref,
    shipperId,
    isHall,
    fcmToken,
    finalTgChatId,
    isBot,
    webApp,
    router,
    sessionData.order_type,
  ])

  useEffect(() => {
    const fetchData = async () => {
      const platformId = getPlatform({
        is_hall: String(isHall),
        platform_id: source,
      })

      const deviceId = await getDeviceId()

      setCookie('platform_id', platformId, SESSION_COOKIE_OPTIONS)
      dispatch(
        sessionActions.save({
          platform_id: platformId,
          device_id: deviceId,
        }),
      )

      // Defer non-critical tracking to reduce TBT
      setTimeout(() => {
        trackEnterance(
          {
            fcm_token: fcmToken || '',
            device_id: deviceId || '',
            platform_id: platformId,
            source_id: source,
            tg_chat_id: finalTgChatId,
          },
          ref,
          dispatch,
        )
      }, 2000)
    }

    fetchData()
  }, [source, isHall, fcmToken, finalTgChatId, ref, dispatch])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const cookieShipperId = getCookie('shipper_id')
    if (
      cookieShipperId &&
      ((sessionData.shipper_id && sessionData.shipper_id !== cookieShipperId) ||
        (settings.data?.shipper_id &&
          settings.data.shipper_id !== cookieShipperId))
    ) {
      dispatch(resetAppState())
      router.refresh()
    }
  }, [
    getCookie,
    settings.data?.shipper_id,
    sessionData.shipper_id,
    sessionData.menu_id,
    common.branches?.[shipperId]?.menu_id,
    dispatch,
    router,
  ])

  return null
}
